import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, X, MapPin, Clock, Users, CalendarDays, Waves, Star, Trash2, Sparkles, Send, Loader2, Plane, ChevronDown, ChevronUp } from 'lucide-react';
import PageShell from '../components/PageShell';
import SpotSearchSelect from '../components/SpotSearchSelect';
import { motion, AnimatePresence } from 'framer-motion';
import ProGate from '../components/ProGate';
import { useSubscription } from '../contexts/SubscriptionContext';
import { authFetch, API } from '../utils/api';


function getMonthData(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return { firstDay, daysInMonth };
}

function formatMonth(year, month) {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export default function Calendar({ user }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [pastSessions, setPastSessions] = useState([]);
  const [plannedSessions, setPlannedSessions] = useState([]);
  const [crewSessions, setCrewSessions] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [showLogForm, setShowLogForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [crew, setCrew] = useState([]);
  const [stats, setStats] = useState({ sessions: 0, planned: 0, water_time_minutes: 0 });
  const [planForm, setPlanForm] = useState({ date: '', time: '', location: '', notes: '', crew_session: false, invited_crew: [] });
  const [logForm, setLogForm] = useState({ date: '', location: '', duration_minutes: 60, wave_height: '', board: '', notes: '', rating: 3 });

  // Swell AI scheduling state
  const [showSwell, setShowSwell] = useState(false);
  const [swellInput, setSwellInput] = useState('');
  const [swellLoading, setSwellLoading] = useState(false);
  const [swellMessages, setSwellMessages] = useState([]);

  // Smart suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsEnabled, setSuggestionsEnabled] = useState(false);
  const [suggestionsSpot, setSuggestionsSpot] = useState('');
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Trips state
  const [trips, setTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(true);
  const [showTripForm, setShowTripForm] = useState(false);
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [briefings, setBriefings] = useState({});
  const [briefingLoading, setBriefingLoading] = useState({});
  const [tripDest, setTripDest] = useState('');
  const [tripDestLabel, setTripDestLabel] = useState('');
  const [tripStart, setTripStart] = useState('');
  const [tripEnd, setTripEnd] = useState('');
  const [tripNotes, setTripNotes] = useState('');
  const [tripSuggestions, setTripSuggestions] = useState([]);
  const [tripSugLoading, setTripSugLoading] = useState(false);
  const tripSearchTimer = React.useRef(null);

  const loadMonth = async () => {
    setLoading(true);
    try {
      const m = formatMonth(year, month);
      const [calRes, statsRes] = await Promise.all([
        authFetch(`${API}/api/calendar?month=${m}`),
        authFetch(`${API}/api/calendar/stats?month=${m}`),
      ]);
      if (calRes.ok) {
        const data = await calRes.json();
        setPastSessions(data.past_sessions || []);
        setPlannedSessions(data.planned_sessions || []);
        setCrewSessions(data.crew_sessions || []);
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch {} finally { setLoading(false); }
  };

  const loadCrew = async () => {
    try {
      const res = await authFetch(`${API}/api/crew`);
      if (res.ok) setCrew((await res.json()).crew || []);
    } catch {}
  };

  useEffect(() => { loadMonth(); }, [year, month]);
  useEffect(() => { loadCrew(); loadSuggestions(); loadTrips(); }, []);

  const loadSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const res = await authFetch(`${API}/api/calendar/suggestions`);
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
        setSuggestionsEnabled(data.enabled || false);
        setSuggestionsSpot(data.spot || '');
      }
    } catch {} finally { setSuggestionsLoading(false); }
  };

  const loadTrips = async () => {
    try {
      const res = await authFetch(`${API}/api/trips`);
      if (res.ok) setTrips((await res.json()).trips || []);
    } catch {} finally { setTripsLoading(false); }
  };

  const handleTripDestSearch = (val) => {
    setTripDest(val);
    setTripDestLabel('');
    clearTimeout(tripSearchTimer.current);
    if (val.trim().length < 2) { setTripSuggestions([]); return; }
    setTripSugLoading(true);
    tripSearchTimer.current = setTimeout(async () => {
      try {
        const res = await authFetch(`${API}/api/city-search?q=${encodeURIComponent(val.trim())}`);
        if (res.ok) setTripSuggestions((await res.json()).results || []);
      } catch {} finally { setTripSugLoading(false); }
    }, 350);
  };

  const handleCreateTrip = async () => {
    if (!tripDest || !tripStart) return;
    await authFetch(`${API}/api/trips`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },       body: JSON.stringify({ destination: tripDest, destination_label: tripDestLabel || tripDest, start_date: tripStart, end_date: tripEnd || tripStart, notes: tripNotes }),
    });
    loadTrips();
    setShowTripForm(false);
    setTripDest(''); setTripDestLabel(''); setTripStart(''); setTripEnd(''); setTripNotes('');
  };

  const handleDeleteTrip = async (tripId) => {
    await authFetch(`${API}/api/trips/${tripId}`, { method: 'DELETE',  });
    loadTrips();
  };

  const toggleBriefing = async (tripId) => {
    if (expandedTrip === tripId) { setExpandedTrip(null); return; }
    setExpandedTrip(tripId);
    if (briefings[tripId]) return;
    setBriefingLoading(p => ({ ...p, [tripId]: true }));
    try {
      const res = await authFetch(`${API}/api/trips/${tripId}/briefing`);
      if (res.ok) {
        const data = await res.json();
        setBriefings(p => ({ ...p, [tripId]: data }));
      }
    } catch {} finally { setBriefingLoading(p => ({ ...p, [tripId]: false })); }
  };

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const { firstDay, daysInMonth } = getMonthData(year, month);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const dateMap = {};
  pastSessions.forEach(s => {
    if (!dateMap[s.date]) dateMap[s.date] = { past: [], planned: [], crew: [] };
    dateMap[s.date].past.push(s);
  });
  plannedSessions.forEach(s => {
    if (!dateMap[s.date]) dateMap[s.date] = { past: [], planned: [], crew: [] };
    dateMap[s.date].planned.push(s);
  });
  crewSessions.forEach(s => {
    if (!dateMap[s.date]) dateMap[s.date] = { past: [], planned: [], crew: [] };
    dateMap[s.date].crew.push(s);
  });

  // Build suggestions map by date
  const suggestionsMap = {};
  suggestions.forEach(s => {
    if (s.date) suggestionsMap[s.date] = s;
  });

  const selectedDateStr = selectedDay ? `${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}` : null;
  const selectedEvents = selectedDateStr ? dateMap[selectedDateStr] : null;
  const isPastDate = selectedDateStr ? selectedDateStr <= todayStr : false;

  const handleAddPlanned = async (e) => {
    e.preventDefault();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    try {
      const res = await authFetch(`${API}/api/calendar/planned`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...planForm, date: dateStr }),
      });
      if (res.ok) {
        setShowPlanForm(false);
        setPlanForm({ date: '', time: '', location: '', notes: '', crew_session: false, invited_crew: [] });
        loadMonth();
      }
    } catch {}
  };

  const handleLogSession = async (e) => {
    e.preventDefault();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    try {
      const res = await authFetch(`${API}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...logForm, date: dateStr }),
      });
      if (res.ok) {
        setShowLogForm(false);
        setLogForm({ date: '', location: '', duration_minutes: 60, wave_height: '', board: '', notes: '', rating: 3 });
        loadMonth();
      }
    } catch {}
  };

  const handleDeletePlanned = async (planId) => {
    await authFetch(`${API}/api/calendar/planned/${planId}`, { method: 'DELETE',  });
    loadMonth();
  };

  const handleSwellSchedule = async (e) => {
    e.preventDefault();
    if (!swellInput.trim()) return;
    const userMsg = swellInput.trim();
    setSwellMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setSwellInput('');
    setSwellLoading(true);
    try {
      const res = await authFetch(`${API}/api/calendar/ai-schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: userMsg }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.ok) {
          setSwellMessages(prev => [...prev, { role: 'swell', text: data.message, session: data.session }]);
          loadMonth();
        } else {
          setSwellMessages(prev => [...prev, { role: 'swell', text: data.message }]);
        }
      } else {
        setSwellMessages(prev => [...prev, { role: 'swell', text: "Something went sideways — try again?" }]);
      }
    } catch {
      setSwellMessages(prev => [...prev, { role: 'swell', text: "Network hiccup — give it another shot" }]);
    } finally { setSwellLoading(false); }
  };

  const { isPro } = useSubscription();

  return (
    <PageShell title="Calendar">
      {!isPro ? (
        <div className="p-5">
          <ProGate feature="calendar" />
        </div>
      ) : (
      <div className="p-5" data-testid="calendar-page">
        {/* Month Stats Bar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 bg-bg-subtle/60 border border-white/5 rounded-xl px-3 py-2 text-center" data-testid="cal-stat-sessions">
            <p className="text-lg font-bold text-primary">{stats.sessions}</p>
            <p className="text-[9px] text-muted-foreground">sessions</p>
          </div>
          <div className="flex-1 bg-bg-subtle/60 border border-white/5 rounded-xl px-3 py-2 text-center" data-testid="cal-stat-watertime">
            <p className="text-lg font-bold text-secondary">{Math.round((stats.water_time_minutes || 0) / 60)}h</p>
            <p className="text-[9px] text-muted-foreground">water time</p>
          </div>
          <div className="flex-1 bg-bg-subtle/60 border border-white/5 rounded-xl px-3 py-2 text-center" data-testid="cal-stat-planned">
            <p className="text-lg font-bold text-muted-foreground">{stats.planned}</p>
            <p className="text-[9px] text-muted-foreground">planned</p>
          </div>
        </div>

        {/* Ask Swell Scheduling */}
        <div className="mb-4">
          <button
            data-testid="cal-swell-toggle"
            onClick={() => setShowSwell(!showSwell)}
            className={`w-full border rounded-2xl px-4 py-3 flex items-center gap-3 transition-all ${
              showSwell
                ? 'bg-primary/10 border-primary/30'
                : 'bg-bg-subtle/50 border-white/5 hover:border-primary/20'
            }`}
          >
            <Sparkles className={`w-4 h-4 flex-shrink-0 ${showSwell ? 'text-primary' : 'text-primary/50'}`} />
            <div className="flex-1 text-left">
              <p className="text-sm font-medium">Ask Swell to schedule</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">"plan a trip to Bali next month" or "dawn patrol at Trestles tomorrow"</p>
            </div>
            {showSwell ? <X className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground/40" />}
          </button>

          <AnimatePresence>
            {showSwell && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="mt-2 bg-bg-subtle/40 border border-white/5 rounded-2xl p-3 space-y-2" data-testid="swell-schedule-panel">
                  {/* Messages */}
                  {swellMessages.length > 0 && (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {swellMessages.map((m, i) => (
                        <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[85%] rounded-xl px-3 py-2 text-xs ${
                              m.role === 'user'
                                ? 'bg-primary/15 text-primary'
                                : 'bg-white/5 text-foreground'
                            }`}
                            data-testid={`swell-msg-${i}`}
                          >
                            {m.text}
                            {m.session && (
                              <div className="mt-1.5 flex items-center gap-2 text-[10px] text-muted-foreground">
                                <MapPin className="w-2.5 h-2.5" />
                                <span>{m.session.location}</span>
                                <span className="opacity-40">|</span>
                                <span>{m.session.date}</span>
                                {m.session.time && (
                                  <>
                                    <span className="opacity-40">|</span>
                                    <Clock className="w-2.5 h-2.5" />
                                    <span>{m.session.time}</span>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Input */}
                  <form onSubmit={handleSwellSchedule} className="flex gap-2">
                    <input
                      data-testid="swell-schedule-input"
                      type="text"
                      value={swellInput}
                      onChange={e => setSwellInput(e.target.value)}
                      placeholder="e.g. sunset session at Pipeline Friday"
                      disabled={swellLoading}
                      className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-primary/50 transition-colors placeholder:text-muted-foreground/30 disabled:opacity-50"
                    />
                    <button
                      type="submit"
                      data-testid="swell-schedule-send"
                      disabled={swellLoading || !swellInput.trim()}
                      className="bg-primary text-primary-foreground rounded-xl px-3 py-2.5 hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center gap-1.5"
                    >
                      {swellLoading ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Send className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </form>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Month Nav */}
        <div className="flex items-center justify-between mb-4">
          <button data-testid="cal-prev-month" onClick={prevMonth} className="p-1.5 hover:bg-white/10 rounded-full transition-colors">
            <ChevronLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <h3 className="font-heading text-base font-semibold" data-testid="cal-month-label">
            {MONTH_NAMES[month]} {year}
          </h3>
          <button data-testid="cal-next-month" onClick={nextMonth} className="p-1.5 hover:bg-white/10 rounded-full transition-colors">
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {DAY_LABELS.map((d, i) => (
            <div key={i} className="text-center text-[10px] text-muted-foreground/60 font-medium py-1">{d}</div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const events = dateMap[dateStr];
            const suggestion = suggestionsMap[dateStr];
            const isToday = dateStr === todayStr;
            const isSelected = day === selectedDay;
            const hasPast = events?.past?.length > 0;
            const hasPlanned = events?.planned?.length > 0 || events?.crew?.length > 0;
            const hasSuggestion = !!suggestion;

            return (
              <button
                key={day}
                data-testid={`cal-day-${day}`}
                onClick={() => setSelectedDay(day === selectedDay ? null : day)}
                className={`aspect-square rounded-xl flex flex-col items-center justify-center gap-0.5 text-xs transition-all relative
                  ${isSelected ? 'bg-primary/20 border border-primary/40' : hasSuggestion ? 'bg-amber-400/[0.06] hover:bg-amber-400/10' : 'hover:bg-white/5'}
                  ${isToday ? 'font-bold text-primary' : ''}
                `}
              >
                {hasSuggestion && (
                  <Sparkles className="absolute top-0.5 right-0.5 w-2 h-2 text-amber-400/60" />
                )}
                <span>{day}</span>
                {(hasPast || hasPlanned) && (
                  <div className="flex gap-0.5">
                    {hasPast && <div className="w-1 h-1 rounded-full bg-secondary" />}
                    {hasPlanned && <div className="w-1 h-1 rounded-full bg-primary" />}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 justify-center mb-4 text-[10px] text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-secondary" />
            <span>logged</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <span>planned</span>
          </div>
          {suggestionsEnabled && suggestions.length > 0 && (
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-2.5 h-2.5 text-amber-400/60" />
              <span>swell's pick</span>
            </div>
          )}
        </div>

        {/* Swell's Picks - Smart Suggestions */}
        {suggestionsEnabled && suggestions.length > 0 && (
          <div className="mb-4" data-testid="swell-picks-section">
            <div className="flex items-center gap-2 mb-2.5">
              <Sparkles className="w-3.5 h-3.5 text-amber-400" />
              <p className="text-[10px] uppercase tracking-widest text-amber-400/70 font-bold">Swell's Picks</p>
              {suggestionsSpot && (
                <span className="text-[9px] text-muted-foreground/40 ml-auto">{suggestionsSpot}</span>
              )}
            </div>
            <div className="space-y-2">
              {suggestions.map((s, i) => {
                const scoreColor = s.score >= 8 ? 'text-green-400 bg-green-500/10 border-green-500/20'
                  : s.score >= 6 ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                  : 'text-muted-foreground bg-white/5 border-white/10';
                const vibeEmoji = s.vibe === 'pumping' ? '🔥' : s.vibe === 'fun' ? '🤙' : s.vibe === 'mellow' ? '😌' : s.vibe === 'challenging' ? '💪' : '🌊';
                return (
                  <motion.div
                    key={`suggestion-${s.date}-${i}`}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="bg-bg-subtle/60 border border-amber-400/10 rounded-xl p-3 flex items-center gap-3"
                    data-testid={`suggestion-${i}`}
                  >
                    <div className={`w-10 h-10 rounded-lg border flex flex-col items-center justify-center flex-shrink-0 ${scoreColor}`}>
                      <span className="text-sm font-bold leading-none">{s.score}</span>
                      <span className="text-[7px] opacity-60">/10</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-medium">
                          {new Date(s.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                        </p>
                        <span className="text-[9px] text-muted-foreground">{s.time_window}</span>
                        <span className="text-xs ml-auto">{vibeEmoji}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground/70 mt-0.5 truncate">{s.reason}</p>
                      <div className="flex items-center gap-3 mt-1 text-[9px] text-muted-foreground/50">
                        {s.wave_height_ft && <span>{s.wave_height_ft}ft</span>}
                        {s.wind && <span>{s.wind}</span>}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}

        {/* Selected Day Detail */}
        <AnimatePresence>
          {selectedDay && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="bg-bg-subtle/80 border border-white/10 rounded-2xl p-4"
              data-testid="cal-day-detail"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium">
                  {MONTH_NAMES[month]} {selectedDay}
                </p>
                <div className="flex items-center gap-2">
                  {isPastDate && (
                    <button
                      data-testid="cal-log-session-btn"
                      onClick={() => { setShowLogForm(!showLogForm); setShowPlanForm(false); }}
                      className="text-[10px] text-secondary font-bold inline-flex items-center gap-1 hover:opacity-80"
                    >
                      {showLogForm ? <X className="w-3 h-3" /> : <Waves className="w-3 h-3" />}
                      {showLogForm ? 'Cancel' : 'Log session'}
                    </button>
                  )}
                  <button
                    data-testid="cal-add-planned-btn"
                    onClick={() => { setShowPlanForm(!showPlanForm); setShowLogForm(false); }}
                    className="text-[10px] text-primary font-bold inline-flex items-center gap-1 hover:opacity-80"
                  >
                    {showPlanForm ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    {showPlanForm ? 'Cancel' : 'Plan'}
                  </button>
                </div>
              </div>

              {/* Log Session Form */}
              <AnimatePresence>
                {showLogForm && (
                  <motion.form
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden mb-3"
                    onSubmit={handleLogSession}
                    data-testid="cal-log-form"
                  >
                    <div className="space-y-2 pb-3 border-b border-white/5 mb-3">
                      <p className="text-[10px] uppercase tracking-widest text-secondary font-medium">Log a past session</p>
                      <SpotSearchSelect
                        value={logForm.location}
                        onChange={val => setLogForm({ ...logForm, location: val })}
                        placeholder="search for a spot..."
                        testId="log-location-search"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input data-testid="log-duration-input" type="number" value={logForm.duration_minutes} onChange={e => setLogForm({ ...logForm, duration_minutes: parseInt(e.target.value) || 0 })} placeholder="minutes" className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs outline-none focus:border-secondary/50 placeholder:text-muted-foreground/40" />
                        <input data-testid="log-wave-height-input" type="text" value={logForm.wave_height} onChange={e => setLogForm({ ...logForm, wave_height: e.target.value })} placeholder="wave height (e.g. 3-4ft)" className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs outline-none focus:border-secondary/50 placeholder:text-muted-foreground/40" />
                      </div>
                      <input data-testid="log-board-input" type="text" value={logForm.board} onChange={e => setLogForm({ ...logForm, board: e.target.value })} placeholder="board (e.g. 6'2 shortboard)" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs outline-none focus:border-secondary/50 placeholder:text-muted-foreground/40" />
                      <input data-testid="log-notes-input" type="text" value={logForm.notes} onChange={e => setLogForm({ ...logForm, notes: e.target.value })} placeholder="notes..." className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs outline-none focus:border-secondary/50 placeholder:text-muted-foreground/40" />
                      {/* Rating */}
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground">Rating:</span>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map(r => (
                            <button
                              key={r}
                              type="button"
                              data-testid={`log-rating-${r}`}
                              onClick={() => setLogForm({ ...logForm, rating: r })}
                              className="p-0.5"
                            >
                              <Star className={`w-4 h-4 ${r <= logForm.rating ? 'text-secondary fill-secondary' : 'text-muted/50'}`} />
                            </button>
                          ))}
                        </div>
                      </div>
                      <button data-testid="log-submit-btn" type="submit" className="w-full bg-secondary text-secondary-foreground text-xs font-bold py-2 rounded-xl hover:opacity-90 transition-opacity">
                        Log Session
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>

              {/* Plan Session Form */}
              <AnimatePresence>
                {showPlanForm && (
                  <motion.form
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden mb-3"
                    onSubmit={handleAddPlanned}
                    data-testid="cal-plan-form"
                  >
                    <div className="space-y-2 pb-3">
                      <p className="text-[10px] uppercase tracking-widest text-primary font-medium">Plan a session</p>
                      <div className="grid grid-cols-2 gap-2">
                        <input data-testid="plan-time-input" type="time" value={planForm.time} onChange={e => setPlanForm({ ...planForm, time: e.target.value })} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs outline-none focus:border-primary/50" />
                        <div className="col-span-1">
                          <SpotSearchSelect
                            value={planForm.location}
                            onChange={val => setPlanForm({ ...planForm, location: val })}
                            placeholder="search spot..."
                            testId="plan-location-search"
                          />
                        </div>
                      </div>
                      <input data-testid="plan-notes-input" type="text" value={planForm.notes} onChange={e => setPlanForm({ ...planForm, notes: e.target.value })} placeholder="notes..." className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40" />

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          data-testid="plan-crew-toggle"
                          type="checkbox"
                          checked={planForm.crew_session}
                          onChange={e => setPlanForm({ ...planForm, crew_session: e.target.checked, invited_crew: e.target.checked ? planForm.invited_crew : [] })}
                          className="accent-primary w-3.5 h-3.5"
                        />
                        <span className="text-xs text-muted-foreground">Crew session</span>
                      </label>

                      {planForm.crew_session && crew.length > 0 && (
                        <div className="space-y-1 pl-5">
                          {crew.map(m => (
                            <label key={m.crew_user_id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={planForm.invited_crew.includes(m.crew_user_id)}
                                onChange={e => {
                                  const list = e.target.checked
                                    ? [...planForm.invited_crew, m.crew_user_id]
                                    : planForm.invited_crew.filter(id => id !== m.crew_user_id);
                                  setPlanForm({ ...planForm, invited_crew: list });
                                }}
                                className="accent-primary w-3 h-3"
                              />
                              <span className="text-[11px] text-muted-foreground">{m.crew_name}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      <button data-testid="plan-submit-btn" type="submit" className="w-full bg-primary text-primary-foreground text-xs font-bold py-2 rounded-xl hover:opacity-90 transition-opacity">
                        Save
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>

              {/* Events for this day */}
              {selectedEvents?.past?.length > 0 && (
                <div className="space-y-2 mb-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Logged Sessions</p>
                  {selectedEvents.past.map((s, i) => (
                    <div key={s.session_id || i} className="bg-white/3 rounded-xl p-3 space-y-1">
                      <div className="flex items-center gap-2 text-xs">
                        <div className="w-1.5 h-1.5 rounded-full bg-secondary flex-shrink-0" />
                        <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        <span className="font-medium truncate">{s.location}</span>
                        {s.duration_minutes > 0 && <span className="text-muted-foreground ml-auto flex-shrink-0">{s.duration_minutes}min</span>}
                      </div>
                      {(s.wave_height || s.board) && (
                        <div className="flex items-center gap-3 text-[10px] text-muted-foreground pl-5">
                          {s.wave_height && <span>{s.wave_height}</span>}
                          {s.board && <span>{s.board}</span>}
                        </div>
                      )}
                      {s.rating > 0 && (
                        <div className="flex gap-0.5 pl-5">
                          {[1, 2, 3, 4, 5].map(r => (
                            <Star key={r} className={`w-2.5 h-2.5 ${r <= s.rating ? 'text-secondary fill-secondary' : 'text-muted/30'}`} />
                          ))}
                        </div>
                      )}
                      {s.notes && <p className="text-[10px] text-muted-foreground/70 pl-5 italic">"{s.notes}"</p>}
                    </div>
                  ))}
                </div>
              )}

              {selectedEvents?.planned?.length > 0 && (
                <div className="space-y-2 mb-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Planned</p>
                  {selectedEvents.planned.map((s, i) => (
                    <div key={s.plan_id || i} className="flex items-center gap-2 text-xs group">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      {s.time && <><Clock className="w-3 h-3 text-muted-foreground flex-shrink-0" /><span>{s.time}</span></>}
                      <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{s.location}</span>
                      {s.crew_session && <Users className="w-3 h-3 text-primary flex-shrink-0" />}
                      {s.source === 'swell_ai' && <Sparkles className="w-3 h-3 text-primary/50 flex-shrink-0" title="Scheduled by Swell" />}
                      <button
                        data-testid={`delete-plan-${s.plan_id}`}
                        onClick={() => handleDeletePlanned(s.plan_id)}
                        className="ml-auto opacity-0 group-hover:opacity-100 p-0.5 hover:bg-white/10 rounded transition-all flex-shrink-0"
                      >
                        <Trash2 className="w-3 h-3 text-muted-foreground" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {selectedEvents?.crew?.length > 0 && (
                <div className="space-y-2 mb-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Crew Invites</p>
                  {selectedEvents.crew.map((s, i) => (
                    <div key={s.plan_id || i} className="flex items-center gap-2 text-xs">
                      <Users className="w-3 h-3 text-primary flex-shrink-0" />
                      <span className="text-muted-foreground">{s.organizer_name}</span>
                      {s.time && <span>{s.time}</span>}
                      <MapPin className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{s.location}</span>
                    </div>
                  ))}
                </div>
              )}

              {!selectedEvents?.past?.length && !selectedEvents?.planned?.length && !selectedEvents?.crew?.length && !showPlanForm && !showLogForm && (
                <p className="text-[11px] text-muted-foreground/60 text-center py-2">no sessions</p>
              )}

              {/* Smart suggestion for this day */}
              {suggestionsMap[selectedDateStr] && (
                <div className="mt-2 bg-amber-500/[0.04] border border-amber-400/10 rounded-xl p-3" data-testid="day-suggestion">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-3 h-3 text-amber-400" />
                    <span className="text-[10px] font-bold text-amber-400/80 uppercase tracking-wider">Swell's pick</span>
                    <span className="text-xs font-bold text-amber-400 ml-auto">{suggestionsMap[selectedDateStr].score}/10</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground/80">{suggestionsMap[selectedDateStr].reason}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-[9px] text-muted-foreground/50">
                    <span>{suggestionsMap[selectedDateStr].time_window}</span>
                    {suggestionsMap[selectedDateStr].wave_height_ft && <span>{suggestionsMap[selectedDateStr].wave_height_ft}ft</span>}
                    {suggestionsMap[selectedDateStr].wind && <span>{suggestionsMap[selectedDateStr].wind}</span>}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Surf Trips Section */}
        <div className="mt-5" data-testid="calendar-trips-section">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Plane className="w-4 h-4 text-secondary" />
              <p className="text-sm font-bold">Surf Trips</p>
            </div>
            <button
              data-testid="cal-trip-add-btn"
              onClick={() => setShowTripForm(!showTripForm)}
              className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
            >
              {showTripForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4 text-secondary" />}
            </button>
          </div>

          {/* Add Trip Form */}
          <AnimatePresence>
            {showTripForm && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-3">
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-4 space-y-3" data-testid="cal-trip-form">
                  <p className="text-[9px] uppercase tracking-widest text-muted-foreground/50 font-bold">New Trip</p>
                  <div className="relative">
                    <input
                      data-testid="cal-trip-dest-input"
                      value={tripDest}
                      onChange={e => handleTripDestSearch(e.target.value)}
                      placeholder="Search a surf destination..."
                      autoComplete="off"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs outline-none focus:border-secondary/50 placeholder:text-muted-foreground/30"
                    />
                    {tripSugLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="w-3 h-3 border-2 border-secondary/40 border-t-secondary rounded-full animate-spin" /></div>}
                    {tripSuggestions.length > 0 && (
                      <div className="absolute inset-x-0 top-full mt-1 bg-bg-paper border border-white/10 rounded-lg shadow-xl z-30 overflow-hidden max-h-40 overflow-y-auto">
                        {tripSuggestions.map((sug, i) => (
                          <button key={i} onClick={() => { setTripDest(sug.city); setTripDestLabel(sug.label); setTripSuggestions([]); }}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-secondary/10 transition-colors border-b border-white/[0.04] last:border-0 flex items-center gap-2">
                            <MapPin className={`w-3 h-3 flex-shrink-0 ${sug.coastal ? 'text-secondary' : 'text-muted-foreground/40'}`} />
                            <span className="truncate flex-1">{sug.label}</span>
                            {sug.coastal && <span className="text-[8px] text-secondary/70 bg-secondary/10 rounded-full px-1.5 py-0.5 flex-shrink-0">surf</span>}
                          </button>
                        ))}
                      </div>
                    )}
                    {tripDestLabel && <p className="text-[9px] text-secondary/60 mt-0.5">{tripDestLabel}</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[9px] text-muted-foreground/60 mb-0.5 block">Start</label>
                      <input data-testid="cal-trip-start" type="date" value={tripStart} onChange={e => setTripStart(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs outline-none focus:border-secondary/50" />
                    </div>
                    <div>
                      <label className="text-[9px] text-muted-foreground/60 mb-0.5 block">End</label>
                      <input data-testid="cal-trip-end" type="date" value={tripEnd} onChange={e => setTripEnd(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs outline-none focus:border-secondary/50" />
                    </div>
                  </div>
                  <input data-testid="cal-trip-notes" value={tripNotes} onChange={e => setTripNotes(e.target.value)}
                    placeholder="e.g. Staying at Uluwatu, bringing the fish"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs outline-none focus:border-secondary/50 placeholder:text-muted-foreground/30" />
                  <button data-testid="cal-trip-save-btn" onClick={handleCreateTrip} disabled={!tripDest || !tripStart}
                    className="w-full bg-secondary text-secondary-foreground text-xs font-bold py-2.5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-40">
                    Save Trip
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Trip List */}
          {tripsLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground/30" /></div>
          ) : trips.length === 0 ? (
            <div className="text-center py-8 bg-bg-subtle/30 rounded-2xl border border-white/[0.03]">
              <Plane className="w-6 h-6 text-muted-foreground/20 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground/50 mb-1">no trips planned yet</p>
              <p className="text-[10px] text-muted-foreground/30">ask Swell above or tap + to plan one</p>
            </div>
          ) : (
            <div className="space-y-2">
              {trips.filter(t => t.status !== 'past').map((trip, i) => {
                const dayLabel = trip.days_until === 0 ? 'Today!' : trip.days_until === 1 ? 'Tomorrow' : `${trip.days_until} days`;
                const statusCls = trip.status === 'today' ? 'text-green-400 bg-green-400/10' : trip.status === 'soon' ? 'text-secondary bg-secondary/10' : 'text-primary bg-primary/10';
                const forecast = briefings[trip.trip_id]?.forecast;
                return (
                  <motion.div key={trip.trip_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                    className="bg-bg-subtle/50 border border-white/[0.05] rounded-2xl overflow-hidden" data-testid={`cal-trip-${trip.trip_id}`}>
                    <div className="flex items-center gap-3 px-4 py-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-semibold truncate">{trip.destination}</p>
                          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-md ${statusCls}`}>{dayLabel}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[9px] text-muted-foreground/50">
                          <CalendarDays className="w-2.5 h-2.5" />
                          <span>{trip.start_date}{trip.end_date && trip.end_date !== trip.start_date ? ` — ${trip.end_date}` : ''}</span>
                        </div>
                        {trip.notes && <p className="text-[9px] text-muted-foreground/40 mt-0.5 italic truncate">{trip.notes}</p>}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => toggleBriefing(trip.trip_id)} className="p-1.5 hover:bg-white/5 rounded-lg transition-colors" data-testid={`cal-trip-briefing-${trip.trip_id}`}>
                          {expandedTrip === trip.trip_id ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground/40" /> : <ChevronDown className="w-3.5 h-3.5 text-secondary/60" />}
                        </button>
                        <button onClick={() => handleDeleteTrip(trip.trip_id)} className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors">
                          <Trash2 className="w-3 h-3 text-red-400/40" />
                        </button>
                      </div>
                    </div>
                    <AnimatePresence>
                      {expandedTrip === trip.trip_id && (
                        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                          <div className="border-t border-white/[0.04] px-4 py-3">
                            {briefingLoading[trip.trip_id] ? (
                              <div className="flex items-center gap-2 py-2">
                                <Loader2 className="w-3 h-3 animate-spin text-secondary/50" />
                                <span className="text-[10px] text-muted-foreground/50">Fetching forecast...</span>
                              </div>
                            ) : forecast ? (
                              <div>
                                <p className="text-xs font-semibold text-secondary mb-2">{forecast.vibe}</p>
                                <div className="grid grid-cols-2 gap-2 mb-3">
                                  <div className="bg-white/[0.03] rounded-lg p-2 text-center">
                                    <Waves className="w-3 h-3 mx-auto mb-0.5 text-secondary/60" />
                                    <p className="text-sm font-bold">{forecast.max_wave_ft}'</p>
                                    <p className="text-[8px] text-muted-foreground/40">max wave</p>
                                  </div>
                                  <div className="bg-white/[0.03] rounded-lg p-2 text-center">
                                    <Waves className="w-3 h-3 mx-auto mb-0.5 text-primary/60" />
                                    <p className="text-sm font-bold">{forecast.avg_wave_ft}'</p>
                                    <p className="text-[8px] text-muted-foreground/40">avg wave</p>
                                  </div>
                                </div>
                                {forecast.days?.length > 0 && (
                                  <div className="space-y-1 mb-2">
                                    {forecast.days.slice(0, 5).map(d => (
                                      <div key={d.date} className="flex items-center gap-2 text-[10px]">
                                        <span className="w-14 text-muted-foreground/40 flex-shrink-0">{new Date(d.date + 'T12:00').toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' })}</span>
                                        <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
                                          <div className="h-full bg-secondary/40 rounded-full" style={{ width: `${Math.min(d.wave_height_ft / 10 * 100, 100)}%` }} />
                                        </div>
                                        <span className="w-7 text-right font-medium">{d.wave_height_ft}'</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="text-[10px] text-muted-foreground/50 py-2">{briefings[trip.trip_id]?.message || 'Forecast not available'}</p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}

              {/* Past trips (collapsed) */}
              {trips.filter(t => t.status === 'past').length > 0 && (
                <div className="mt-2">
                  <p className="text-[9px] uppercase tracking-widest text-muted-foreground/30 font-bold mb-1.5">Past</p>
                  <div className="space-y-1 opacity-50">
                    {trips.filter(t => t.status === 'past').map(trip => (
                      <div key={trip.trip_id} className="flex items-center gap-2.5 bg-white/[0.02] border border-white/[0.03] rounded-xl px-3 py-2">
                        <Plane className="w-3 h-3 text-muted-foreground/30 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{trip.destination}</p>
                          <p className="text-[9px] text-muted-foreground/40">{trip.start_date}</p>
                        </div>
                        <button onClick={() => handleDeleteTrip(trip.trip_id)} className="p-1 hover:bg-red-500/10 rounded-md">
                          <Trash2 className="w-3 h-3 text-red-400/40" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Calendar Sync Placeholder */}
        <div className="mt-5 bg-bg-subtle/50 border border-white/5 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <CalendarDays className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">Calendar Sync</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">Google, Microsoft & Apple calendar sync coming soon</p>
            </div>
            <span className="text-[10px] text-muted-foreground/60 bg-white/5 px-2 py-1 rounded-lg flex-shrink-0">Soon</span>
          </div>
        </div>
      </div>
      )}
    </PageShell>
  );
}
